import API_URLS from '../config/apiConfig';
import api from '../interceptors/axiosConfig';
import { buildStartingTicketNumber, getTicketNumberForSoldCount } from '../utils/ticketFactory';
import { handleGameError } from '../utils/errorHandler';
import { apiGet, apiPost } from '../utils/apiUtils';
import type {
  CreateGameRequest,
  Game,
  GameMetadataMismatchesResponse,
  GameResponse,
  GamesMissingPluResponse,
  MissingGamesResponse,
  UpdateGameRequest,
} from '../types/features/games';
import { normalizeIdToInt, normalizePriceToFloat } from '../utils/normalizers/auditTicketNormalizers';
import type {
  ActivatePackRequest,
  ActivatePackResponse,
  ConvertGameToPackOptions,
  ConvertedGamePack,
  LottoSaleRequest,
  LottoSaleResponse,
  LottoDataBackfillRequest,
  LottoDataBackfillResponse,
  PayoutRequest,
  PayoutResponse,
  PosPayoutRequest,
  PosPayoutResponse,
  ReturnPackRequest,
  ReturnPackResponse,
  SaleRequest,
  SaleResponse,
} from '../types/services/games';

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return value === true || value === 1;
};

export type {
  ActivatePackRequest,
  ActivatePackResponse,
  ConvertGameToPackOptions,
  ConvertedGamePack,
  LottoSaleRequest,
  LottoSaleResponse,
  LottoDataBackfillRequest,
  LottoDataBackfillResponse,
  PayoutRequest,
  PayoutResponse,
  PosPayoutRequest,
  PosPayoutResponse,
  ReturnPackRequest,
  ReturnPackResponse,
  SaleRequest,
  SaleResponse,
} from '../types/services/games';

class GamesService {
  private normalizeGame(game: any): Game {
    const normalizedGameId = normalizeIdToInt(game?.game_id);
    const normalizedCost = normalizePriceToFloat(game?.cost_per_ticket);
    const normalizedTicketsPerPack = normalizeIdToInt(game?.tickets_per_pack);

    return {
      ...game,
      game_id: (normalizedGameId ?? game?.game_id) as any,
      cost_per_ticket: (normalizedCost ?? game?.cost_per_ticket ?? 0) as any,
      tickets_per_pack: (normalizedTicketsPerPack ?? game?.tickets_per_pack ?? 0) as any,
      pending_plu_verification: normalizeBoolean(game?.pending_plu_verification),
    };
  }

  // Get all games from local API
  async getAllGames(stateId?: string): Promise<Game[]> {
    try {
      let url = API_URLS.GET_ALL_GAMES;
      if (stateId) {
        url += `?state=${encodeURIComponent(stateId)}`;
      }

      const data = await apiGet(url);
      // If the response is an array, return as is
      if (Array.isArray(data)) {
        return data.map((item) => this.normalizeGame(item));
      }
      // If the response is an object with data property
      if (data && Array.isArray(data.data)) {
        return data.data.map((item: any) => this.normalizeGame(item));
      }
      // Otherwise, return empty array
      return [];
    } catch (error) {
      console.error('Error fetching games:', error);
      handleGameError(error, 'fetch');
      throw error;
    }
  }

  // Get all games with proper API response structure
  async getGames(stateId?: string): Promise<GameResponse> {
    try {
      let url = API_URLS.GET_ALL_GAMES;
      if (stateId) {
        url += `?state=${stateId}`;
      }
      
      const data = await apiGet(url);
      // If the response is an array, wrap it
      if (Array.isArray(data)) {
        return { success: true, data: data.map((item) => this.normalizeGame(item)) };
      }
      // If the response is an object with data property
      if (data && Array.isArray(data.data)) {
        return {
          ...data,
          success: true,
          data: data.data.map((item: any) => this.normalizeGame(item)),
        };
      }
      // Otherwise, treat as error
      return { success: false, error: 'Unexpected response format' };
    } catch (error) {
      console.error('Error fetching games:', error);
      const errorMessage = handleGameError(error, 'fetch');
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async getGamesMissingPlu(): Promise<GamesMissingPluResponse> {
    return apiGet(API_URLS.GET_GAMES_MISSING_PLU);
  }

  async getMissingGames(): Promise<MissingGamesResponse> {
    return apiGet(API_URLS.GET_MISSING_GAMES);
  }

  async getGameMetadataMismatches(): Promise<GameMetadataMismatchesResponse> {
    return apiGet(API_URLS.GET_GAME_METADATA_MISMATCHES);
  }

  // Get game by ID
  async getGameById(id: string): Promise<GameResponse> {
    try {
      const data = await apiGet(`${API_URLS.GET_ALL_GAMES}/${id}`);
      return data;
    } catch (error) {
      console.error('Error fetching game:', error);
      const errorMessage = handleGameError(error, 'fetch');
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Create new game (FormData)
  async createGame(gameData: CreateGameRequest): Promise<GameResponse> {
    try {
      const formData = new FormData();
      
      // Add required fields
      formData.append('game_id', gameData.game_id.toString());
      formData.append('state_code', gameData.state_code.toString());
      formData.append('tickets_per_pack', gameData.tickets_per_pack.toString());
      formData.append('cost_per_ticket', gameData.cost_per_ticket.toString());

      // Add optional fields when provided
      if (gameData.plu !== undefined) formData.append('plu', gameData.plu.toString());
      if (gameData.name !== undefined) formData.append('name', gameData.name);
      if (gameData.start_date !== undefined) formData.append('start_date', gameData.start_date);
      if (gameData.end_date !== undefined) formData.append('end_date', gameData.end_date);
      if (gameData.image_url !== undefined) formData.append('image_url', gameData.image_url);
      if (gameData.pending_plu_verification !== undefined) {
        formData.append('pending_plu_verification', gameData.pending_plu_verification.toString());
      }
      
      // Add image file if provided
      if (gameData.image) {
        formData.append('image', gameData.image);
      }

      const response = await api.post(API_URLS.GET_ALL_GAMES, formData);
      return response.data;
    } catch (error) {
      console.error('Error creating game:', error);
      const errorMessage = handleGameError(error, 'create');
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Update game (FormData)
  async updateGame(id: string, gameData: UpdateGameRequest): Promise<GameResponse> {
    try {
      const formData = new FormData();
      
      // Add all text fields that are provided
      if (gameData.game_id !== undefined) formData.append('game_id', gameData.game_id.toString());
      if (gameData.state_code !== undefined) formData.append('state_code', gameData.state_code.toString());
      if (gameData.plu !== undefined) formData.append('plu', gameData.plu.toString());
      if (gameData.name !== undefined) formData.append('name', gameData.name);
      if (gameData.tickets_per_pack !== undefined) formData.append('tickets_per_pack', gameData.tickets_per_pack.toString());
      if (gameData.cost_per_ticket !== undefined) formData.append('cost_per_ticket', gameData.cost_per_ticket.toString());
      if (gameData.start_date !== undefined) formData.append('start_date', gameData.start_date);
      if (gameData.end_date !== undefined) formData.append('end_date', gameData.end_date);
      if (gameData.image_url !== undefined) formData.append('image_url', gameData.image_url);
      if (gameData.pending_plu_verification !== undefined) {
        formData.append('pending_plu_verification', gameData.pending_plu_verification.toString());
      }
      
      // Add image file if provided
      if (gameData.image) {
        formData.append('image', gameData.image);
      }

      const response = await api.put(API_URLS.UPDATE_GAME(id), formData);
      return response.data;
    } catch (error) {
      console.error('Error updating game:', error);
      const errorMessage = handleGameError(error, 'update');
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Delete game
  async deleteGame(id: string): Promise<GameResponse> {
    try {
      const response = await api.delete(`${API_URLS.GET_ALL_GAMES}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting game:', error);
      const errorMessage = handleGameError(error, 'delete');
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Generate a 7-digit random pack ID
  generatePackId(): string {
    return Math.floor(1000000 + Math.random() * 9000000).toString();
  }

  // Convert Game to ActivatedPack format
  convertGameToPack(
    game: Game,
    packType: 'lotto' | 'scratchers' = 'lotto',
    options?: ConvertGameToPackOptions
  ): ConvertedGamePack {
    const packId = this.generatePackId();
    // Keep game number unpadded; the `barcodeFormat` pattern controls game segment length
    // (supports 0-3, 0-4, 0-5 and other segment lengths).
    const gameNumber = game.game_id.toString();
    const format = options?.barcodeFormat;
    if (!format) {
      throw new Error('Missing barcode format for pack creation');
    }
    const firstTicket = getTicketNumberForSoldCount({
      firstTicketNumber: options?.regionStartingTicketNumber ?? 0,
      ticketsPerPack: game.tickets_per_pack,
      ticketsSold: 0,
      direction: options?.direction,
    });
    const startingTicketNumber = buildStartingTicketNumber({ format, gameNumber, packId, ticketsSold: firstTicket });
    const startingTicketNumberValue = Number(startingTicketNumber);
    
    return {
      pack_id: packId,
      date_activated: new Date().toISOString(),
      game_name: game.name,
      game_number: gameNumber,
      tickets_per_pack: game.tickets_per_pack,
      tickets_sold: 0,
      price_point: Number(game.cost_per_ticket),
      pack_retail_value: Number(game.cost_per_ticket) * game.tickets_per_pack,
      starting_ticket_number: startingTicketNumberValue,
      plu: game.plu || Math.floor(Math.random() * 100000),
      image_url: game.image_url || game.image || '',
      pack_type: packType,
      barcode_format: format,
      direction: options?.direction,
    };
  }

  // Activate a pack via API
  async activatePack(request: ActivatePackRequest): Promise<ActivatePackResponse> {
    try {
      const data = await apiPost(API_URLS.ACTIVATE_PACK, request);
      return {
        success: true,
        message: 'Pack activated successfully',
        pack_id: data.pack_id || request.pack_id,
      };
    } catch (error: any) {
      console.error('Error activating pack:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to activate pack';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Record a ticket sale via API
  async recordSale(request: SaleRequest): Promise<SaleResponse> {
    try {
      const data = await apiPost(API_URLS.SALE, request);
      return {
        success: true,
        message: 'Sale recorded successfully',
        sale_id: data.sale_id || 'unknown',
      };
    } catch (error: any) {
      console.error('Error recording sale:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to record sale';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Record a ticket return via API
  async recordReturn(request: SaleRequest): Promise<SaleResponse> {
    try {
      const data = await apiPost(API_URLS.RETURN, request);
      return {
        success: true,
        message: 'Return recorded successfully',
        sale_id: data.return_id || 'unknown',
      };
    } catch (error: any) {
      console.error('Error recording return:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to record return';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Return a pack via API
  async returnPack(request: ReturnPackRequest): Promise<ReturnPackResponse> {
    try {
      const data = await apiPost(API_URLS.RETURN_PACK, request);
      return {
        success: true,
        message: 'Pack returned successfully',
        return_id: data.return_id || 'unknown',
      };
    } catch (error: any) {
      console.error('Error returning pack:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to return pack';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Process lottery ticket payout (for both lotto and scratchers)
  async payoutLotteryTicket(request: PayoutRequest): Promise<PayoutResponse> {
    try {
      const data = await apiPost(API_URLS.LOTTERY_PAYOUT, request);
      return {
        success: true,
        message: `${request.payout_type} payout processed successfully`,
        payout_id: data.payout_id || 'unknown',
      };
    } catch (error: any) {
      console.error('Error processing lottery payout:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to process lottery payout';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Get POS payout summary
  async getPosPayoutSummary(request: PosPayoutRequest): Promise<PosPayoutResponse> {
    try {
      const data = await apiPost(API_URLS.POS_PAYOUT, request);
      return {
        success: true,
        message: 'POS payout summary retrieved successfully',
        data: data.data || data,
      };
    } catch (error: any) {
      console.error('Error getting POS payout summary:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to get POS payout summary';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Process lotto sale
  async processLottoSale(request: LottoSaleRequest): Promise<LottoSaleResponse> {
    try {
      const data = await apiPost(API_URLS.LOTTO_SALE, request);
      return {
        success: true,
        message: 'Lotto sale processed successfully',
        sale_id: data.sale_id || 'unknown',
      };
    } catch (error: any) {
      console.error('Error processing lotto sale:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to process lotto sale';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  async backfillLottoData(storeId: string, request: LottoDataBackfillRequest): Promise<LottoDataBackfillResponse> {
    try {
      const data = await apiPost(API_URLS.LOTTO_DATA_BACKFILL(storeId), request);
      const successCount = Number(data?.success_count ?? 0);
      const errorCount = Number(data?.error_count ?? 0);
      const dayCount = Number(data?.day_count ?? 0);
      const firstError = Array.isArray(data?.results)
        ? data.results.find((result: any) => result?.status === 'error')?.error
        : undefined;

      if (dayCount > 0 && successCount === 0 && errorCount > 0) {
        const message = firstError
          ? `Lotto data sync failed: ${firstError}`
          : 'Lotto data sync failed for all requested days';
        return {
          success: false,
          message,
          data,
          error: message,
        };
      }

      return {
        success: true,
        message: data?.message || (
          errorCount > 0
            ? `Lotto data sync completed with ${successCount} successful day(s) and ${errorCount} failed day(s)`
            : 'Lotto data sync completed successfully'
        ),
        data,
      };
    } catch (error: any) {
      console.error('Error backfilling lotto data:', error);
      const errorData = error?.data || error?.response?.data || {};
      const msg = errorData.error || errorData.detail || error.message || 'Failed to backfill lotto data';
      return {
        success: false,
        message: msg,
        error: msg,
      };
    }
  }

  // Get game image
  async getGameImage(id: string): Promise<Blob | null> {
    try {
      const response = await api.get(`${API_URLS.GET_ALL_GAMES}/${id}/image`, {
        responseType: 'blob',
      });
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No image found
      }
      console.error('Error fetching game image:', error);
      return null;
    }
  }
}

const gamesService = new GamesService();
export default gamesService;
export type { CreateGameRequest, Game, GameResponse, UpdateGameRequest };
